import { z } from "zod/v4";

// ---------------------------------------------------------------------------
// Agentic Logic integration clients — per-org third-party clients the platform
// calls ON BEHALF of user-authored code (Agentic Logic rules, custom shipping
// providers). User code sees a typed proxy; the credential lives only on the
// trusted side, which resolves a fresh token per call. Adding a provider =
// one catalog entry here + one registry descriptor on the backend + one
// typings block in ig-utils.
// ---------------------------------------------------------------------------

export const AgenticLogicIntegrationClientKeyList = ["ups"] as const;
export const AgenticLogicIntegrationClientKeySchema = z.enum(
  AgenticLogicIntegrationClientKeyList
);
export type AgenticLogicIntegrationClientKey =
  (typeof AgenticLogicIntegrationClientKeyList)[number];

/** The two code domains that may be handed integration clients. */
export const AgenticLogicIntegrationClientDomainList = [
  "shipping_provider",
  "agentic_logic",
] as const;

export const AgenticLogicIntegrationClientDomainSchema = z.enum(
  AgenticLogicIntegrationClientDomainList
);

export type AgenticLogicIntegrationClientDomain =
  (typeof AgenticLogicIntegrationClientDomainList)[number];

// ----- UPS -----------------------------------------------------------------

/**
 * A UPS Rating API request body. The envelope is bounded (`RateRequest` must
 * be an object); the body itself is UPS's own schema and is passed through.
 * The trusted side fills `RateRequest.Shipment.Shipper.ShipperNumber` from the
 * org's validated account number when the caller omits it.
 */
export const UpsRateRequestSchema = z
  .object({
    RateRequest: z.record(z.string(), z.unknown()),
  })
  .strict();

export type UpsRateRequest = z.infer<typeof UpsRateRequestSchema>;

/** UPS's Rating response, passed through as the JSON object UPS returned. */
export const UpsRateResponseSchema = z.record(z.string(), z.unknown());
export type UpsRateResponse = z.infer<typeof UpsRateResponseSchema>;

// ----- Catalog -------------------------------------------------------------

export interface AgenticLogicIntegrationClientOperationDescriptor {
  request: z.ZodType;
  response: z.ZodType;
}

export interface AgenticLogicIntegrationClientDescriptor {
  operations: Readonly<
    Record<string, AgenticLogicIntegrationClientOperationDescriptor>
  >;
}

/**
 * Every client and operation user code may call, with the zod contract of
 * each. The trusted side validates `args` against `request` before dispatch;
 * the Agentic Logic SDK validates on the rule side too so a malformed call
 * fails before spending a read.
 */
export const AGENTIC_LOGIC_INTEGRATION_CLIENT_CATALOG = {
  ups: {
    operations: {
      rate: { request: UpsRateRequestSchema, response: UpsRateResponseSchema },
    },
  },
} as const satisfies Readonly<
  Record<
    AgenticLogicIntegrationClientKey,
    AgenticLogicIntegrationClientDescriptor
  >
>;

/**
 * The catalog contract of one operation, looked up by the plain strings a
 * proxy or a wire call carries, or `undefined` when the catalog names no such
 * client or operation. The catalog is the ONE place that says whether two
 * names mean anything; every validator (the rule-side SDK, the trusted
 * broker) asks it through here.
 */
export function agenticLogicIntegrationClientCatalogOperation(
  client: string,
  operation: string
): AgenticLogicIntegrationClientOperationDescriptor | undefined {
  const key = AgenticLogicIntegrationClientKeySchema.safeParse(client);
  if (!key.success) {
    return undefined;
  }
  const operations: Readonly<
    Record<string, AgenticLogicIntegrationClientOperationDescriptor>
  > = AGENTIC_LOGIC_INTEGRATION_CLIENT_CATALOG[key.data].operations;
  return Object.hasOwn(operations, operation)
    ? operations[operation]
    : undefined;
}

/**
 * The catalog with its zod stripped: client key → operation names. This is
 * what generated bundles embed (they carry no zod), so it is DERIVED from the
 * catalog rather than restated.
 */
export const AGENTIC_LOGIC_INTEGRATION_CLIENT_CATALOG_SHAPE: Readonly<
  Record<AgenticLogicIntegrationClientKey, readonly string[]>
> = Object.freeze(
  Object.fromEntries(
    Object.entries(AGENTIC_LOGIC_INTEGRATION_CLIENT_CATALOG).map(
      ([key, descriptor]) => [
        key,
        Object.freeze(Object.keys(descriptor.operations)),
      ]
    )
  ) as Record<AgenticLogicIntegrationClientKey, readonly string[]>
);

// ----- Grants --------------------------------------------------------------

/** One client and the operations a capability lets its holder call. */
export const AgenticLogicIntegrationClientGrantSchema = z
  .object({
    client: AgenticLogicIntegrationClientKeySchema,
    operations: z.array(z.string().min(1)).min(1),
  })
  .strict();

export type AgenticLogicIntegrationClientGrant = z.infer<
  typeof AgenticLogicIntegrationClientGrantSchema
>;

const ALL_CLIENT_GRANTS: readonly AgenticLogicIntegrationClientGrant[] =
  Object.freeze(
    AgenticLogicIntegrationClientKeyList.map((client) => ({
      client,
      operations: [...AGENTIC_LOGIC_INTEGRATION_CLIENT_CATALOG_SHAPE[client]],
    }))
  );

/**
 * What a mint hands each domain. This is DOMAIN POLICY, not live connection
 * state: whether the org's integration is actually connected is checked on
 * the trusted side at call time (a disconnect after mint must still refuse),
 * so a mint never needs a database read.
 */
export const AGENTIC_LOGIC_INTEGRATION_CLIENT_DOMAIN_GRANTS: Readonly<
  Record<
    AgenticLogicIntegrationClientDomain,
    readonly AgenticLogicIntegrationClientGrant[]
  >
> = Object.freeze({
  shipping_provider: ALL_CLIENT_GRANTS,
  agentic_logic: ALL_CLIENT_GRANTS,
});
